/*
  SOTE: Canada + United Kingdom delivery estimator
  -------------------------------------------------
  Sister script to assets/sote-au-delivery.js (Australia keeps its own file, untouched).
  Renders inside snippets/sote-intl-delivery.liquid for visitors browsing the Canada or UK market.

  What it does
    - Validates a Canadian postal code (A1A 1A1) or a UK postcode (SW1A 1AA) and maps it to a
      province / nation + service tier (metro / regional / remote).
    - Works out the dispatch date from the US warehouse (Robo3PL, Utah) using the same 4pm Eastern
      weekday cutoff the bundle builder uses.
    - Adds the configured transit window in local business days (weekends + that province's / nation's
      public holidays skipped) and prints a date range.
    - Remembers the code in localStorage so it pre-fills across pages.

  All numbers come from data-attributes set by the snippet (Theme settings > Canada market / United Kingdom market).
*/
(function () {
  'use strict';
  if (window.SoteIntlDelivery) { return; }

  var DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /* ---------------------------------------------------------------
     Date helpers (all dates handled as UTC-midnight "calendar days")
     --------------------------------------------------------------- */
  function ymd(y, m, d) { return Date.UTC(y, m, d); }
  function addDays(t, n) { return t + n * 86400000; }
  function dow(t) { return new Date(t).getUTCDay(); }
  function nthWeekday(y, m, weekday, n) { // n-th (1-based) weekday of month
    var t = ymd(y, m, 1);
    var offset = (weekday - dow(t) + 7) % 7;
    return addDays(t, offset + (n - 1) * 7);
  }
  function lastWeekday(y, m, weekday) {
    var t = ymd(y, m + 1, 0); // last day of month
    var offset = (dow(t) - weekday + 7) % 7;
    return addDays(t, -offset);
  }
  function easterSunday(y) { // Anonymous Gregorian algorithm
    var a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
    var f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
    var day = ((h + l - 7 * m + 114) % 31) + 1;
    return ymd(y, month, day);
  }
  function observed(t) { // weekend public holidays roll to Monday
    var d = dow(t);
    if (d === 6) { return addDays(t, 2); }
    if (d === 0) { return addDays(t, 1); }
    return t;
  }

  function xmasSubstitutes(year, list) {
    var c = dow(ymd(year, 11, 25)), b = dow(ymd(year, 11, 26));
    if (c === 6 || c === 0) { list.push(ymd(year, 11, 27)); }
    if (b === 6 || b === 0) { list.push(ymd(year, 11, 28)); }
  }

  /* ===============================================================
     CANADA
     =============================================================== */
  var CA_RE = /^([ABCEGHJ-NPRSTVXY])(\d)([ABCEGHJ-NPRSTV-Z])(\d)([ABCEGHJ-NPRSTV-Z])(\d)$/;
  var CA_PROV = {
    A: ['NL', 'Newfoundland & Labrador'], B: ['NS', 'Nova Scotia'], C: ['PE', 'Prince Edward Island'], E: ['NB', 'New Brunswick'],
    G: ['QC', 'Quebec'], H: ['QC', 'Montreal'], J: ['QC', 'Quebec'],
    K: ['ON', 'Eastern Ontario'], L: ['ON', 'Greater Toronto Area'], M: ['ON', 'Toronto'], N: ['ON', 'Southwestern Ontario'], P: ['ON', 'Northern Ontario'],
    R: ['MB', 'Manitoba'], S: ['SK', 'Saskatchewan'], T: ['AB', 'Alberta'], V: ['BC', 'British Columbia'],
    X: ['NT', 'Northwest Territories & Nunavut'], Y: ['YT', 'Yukon']
  };
  // Forward sortation areas (first 2 characters) worth naming
  var CA_CITY = {
    K1: 'Ottawa', K2: 'Ottawa', G1: 'Quebec City', G2: 'Quebec City', R2: 'Winnipeg', R3: 'Winnipeg',
    S4: 'Regina', S7: 'Saskatoon', T2: 'Calgary', T3: 'Calgary', T5: 'Edmonton', T6: 'Edmonton',
    V5: 'Vancouver', V6: 'Vancouver', V7: 'North Vancouver', V8: 'Victoria', V9: 'Vancouver Island', V3: 'Metro Vancouver', V4: 'Metro Vancouver',
    B3: 'Halifax', E1: 'Moncton', E2: 'Saint John', E3: 'Fredericton', A1: "St. John's", C1: 'Charlottetown',
    N2: 'Kitchener-Waterloo', N5: 'London', N6: 'London', L8: 'Hamilton', L9: 'Hamilton'
  };

  function caNormalize(v) { return (v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6); }
  function caFormat(v) { var n = caNormalize(v); return n.length > 3 ? n.slice(0, 3) + ' ' + n.slice(3) : n; }

  function caClassify(value) {
    var n = caNormalize(value);
    var m = n.match(CA_RE);
    if (!m) { return null; }
    var p = CA_PROV[m[1]];
    var fsa = m[1] + m[2];
    var tier = 'metro';
    if (m[1] === 'X' || m[1] === 'Y') { tier = 'remote'; }
    else if (m[2] === '0') { tier = 'regional'; }                 // second character 0 = rural delivery area
    else if (m[1] === 'A' || m[1] === 'P') { tier = 'regional'; }
    if (fsa === 'A0' && (m[3] === 'P' || m[3] === 'K' || m[3] === 'R')) { tier = 'remote'; } // Labrador
    var label = CA_CITY[fsa] || (m[2] === '0' ? 'rural ' + p[1] : p[1]);
    return { postcode: caFormat(n), state: p[0], tier: tier, label: label };
  }

  function caHolidays(year, prov) {
    var e = easterSunday(year);
    var may24 = ymd(year, 4, 24);
    var victoria = addDays(may24, -((dow(may24) + 6) % 7));       // last Monday on or before 24 May
    var list = [
      observed(ymd(year, 0, 1)),        // New Year's Day
      addDays(e, -2),                   // Good Friday
      victoria,                         // Victoria Day / National Patriots' Day
      observed(ymd(year, 6, 1)),        // Canada Day
      nthWeekday(year, 8, 1, 1),        // Labour Day
      observed(ymd(year, 8, 30)),       // National Day for Truth and Reconciliation
      nthWeekday(year, 9, 1, 2),        // Thanksgiving
      observed(ymd(year, 10, 11)),      // Remembrance Day
      ymd(year, 11, 25),                // Christmas Day
      ymd(year, 11, 26)                 // Boxing Day
    ];
    xmasSubstitutes(year, list);
    if (prov === 'QC') { list.push(observed(ymd(year, 5, 24))); }  // Fete nationale
    else { list.push(nthWeekday(year, 7, 1, 1)); }                 // Civic holiday (first Monday in August)
    return list;
  }

  /* ===============================================================
     UNITED KINGDOM
     =============================================================== */
  var GB_RE = /^([A-Z]{1,2})(\d{1,2}|\d[A-Z])(\d[A-Z]{2})$/;
  var GB_SCOT = { AB: 1, DD: 1, DG: 1, EH: 1, FK: 1, G: 1, HS: 1, IV: 1, KA: 1, KW: 1, KY: 1, ML: 1, PA: 1, PH: 1, TD: 1, ZE: 1 };
  var GB_WALES = { CF: 1, LL: 1, NP: 1, SA: 1, LD: 1 };
  var GB_CITY = {
    E: 'London', EC: 'London', N: 'London', NW: 'London', SE: 'London', SW: 'London', W: 'London', WC: 'London',
    BR: 'Greater London', CR: 'Greater London', HA: 'Greater London', IG: 'Greater London', KT: 'Greater London', RM: 'Greater London',
    SM: 'Greater London', TW: 'Greater London', UB: 'Greater London', EN: 'Greater London', DA: 'Greater London',
    M: 'Manchester', B: 'Birmingham', L: 'Liverpool', LS: 'Leeds', S: 'Sheffield', BS: 'Bristol', NE: 'Newcastle', NG: 'Nottingham',
    LE: 'Leicester', CV: 'Coventry', BD: 'Bradford', HU: 'Hull', SO: 'Southampton', PO: 'Portsmouth', BN: 'Brighton', OX: 'Oxford',
    CB: 'Cambridge', RG: 'Reading', MK: 'Milton Keynes', NR: 'Norwich', PL: 'Plymouth', EX: 'Exeter', YO: 'York', BA: 'Bath',
    G: 'Glasgow', EH: 'Edinburgh', AB: 'Aberdeen', DD: 'Dundee', IV: 'the Highlands', KW: 'Caithness & Orkney', HS: 'the Outer Hebrides', ZE: 'Shetland',
    CF: 'Cardiff', SA: 'Swansea', NP: 'Newport', LL: 'North Wales', BT: 'Northern Ireland', TR: 'Cornwall'
  };
  var GB_CROWN = { JE: 'Jersey', GY: 'Guernsey', IM: 'the Isle of Man' };

  function gbNormalize(v) { return (v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7); }
  function gbFormat(v) { var n = gbNormalize(v); return n.length > 3 ? n.slice(0, n.length - 3) + ' ' + n.slice(n.length - 3) : n; }

  function gbClassify(value) {
    var n = gbNormalize(value);
    var m = n.match(GB_RE);
    if (!m || GB_CROWN[m[1]]) { return null; }
    var area = m[1];
    var district = parseInt(m[2], 10) || 0;
    var nation = area === 'BT' ? 'NIR' : (GB_SCOT[area] ? 'SCT' : (GB_WALES[area] ? 'WLS' : 'ENG'));
    var tier = 'metro';
    if (area === 'HS' || area === 'ZE' || area === 'KW') { tier = 'remote'; }
    else if (area === 'TR' && district >= 21 && district <= 25) { tier = 'remote'; }        // Isles of Scilly
    else if (area === 'IV' || area === 'BT') { tier = 'regional'; }
    else if (area === 'PA' && district >= 20) { tier = 'regional'; }
    else if (area === 'PH' && district >= 15) { tier = 'regional'; }
    else if (area === 'KA' && (district === 27 || district === 28)) { tier = 'regional'; }  // Arran, Cumbrae
    else if (area === 'PO' && district >= 30 && district <= 41) { tier = 'regional'; }      // Isle of Wight
    var label = GB_CITY[area] || (nation === 'SCT' ? 'Scotland' : (nation === 'WLS' ? 'Wales' : 'England'));
    return { postcode: gbFormat(n), state: nation, tier: tier, label: label };
  }

  function gbHolidays(year, nation) {
    var e = easterSunday(year);
    var list = [
      observed(ymd(year, 0, 1)),        // New Year's Day
      addDays(e, -2),                   // Good Friday
      nthWeekday(year, 4, 1, 1),        // Early May bank holiday
      lastWeekday(year, 4, 1),          // Spring bank holiday
      ymd(year, 11, 25),                // Christmas Day
      ymd(year, 11, 26)                 // Boxing Day
    ];
    xmasSubstitutes(year, list);
    if (nation === 'SCT') {
      var jan2 = ymd(year, 0, 2), d2 = dow(jan2);
      // 2 January: Sat -> Mon 4th, Sun -> Mon 3rd, Mon (1 Jan was Sunday, so the 2nd is already taken) -> Tue 3rd
      list.push(d2 === 6 ? ymd(year, 0, 4) : ((d2 === 0 || d2 === 1) ? ymd(year, 0, 3) : jan2));
      list.push(nthWeekday(year, 7, 1, 1));            // Summer bank holiday (first Monday in August)
      list.push(observed(ymd(year, 10, 30)));          // St Andrew's Day
    } else {
      list.push(addDays(e, 1));                        // Easter Monday
      list.push(lastWeekday(year, 7, 1));              // Summer bank holiday (last Monday in August)
    }
    if (nation === 'NIR') {
      list.push(observed(ymd(year, 2, 17)));           // St Patrick's Day
      list.push(observed(ymd(year, 6, 12)));           // Battle of the Boyne
    }
    return list;
  }

  var MARKETS = {
    ca: {
      storageKey: 'sote_ca_postal', classify: caClassify, holidays: caHolidays, normalize: caNormalize, fullLength: 6,
      invalid: 'Enter a valid Canadian postal code (e.g. M5V 2T6 for Toronto).'
    },
    gb: {
      storageKey: 'sote_gb_postcode', classify: gbClassify, holidays: gbHolidays, normalize: gbNormalize, fullLength: 0,
      invalid: 'Enter a valid UK postcode (e.g. SW1A 1AA for London).'
    }
  };

  var holidayCache = {};
  function localHolidays(mk, year, region) {
    var key = mk + year + region;
    if (!holidayCache[key]) { holidayCache[key] = MARKETS[mk].holidays(year, region); }
    return holidayCache[key];
  }

  function usHolidays(year) {
    return [
      observed(ymd(year, 0, 1)),        // New Year's Day
      lastWeekday(year, 4, 1),          // Memorial Day
      observed(ymd(year, 6, 4)),        // Independence Day
      nthWeekday(year, 8, 1, 1),        // Labor Day
      nthWeekday(year, 10, 4, 4),       // Thanksgiving
      addDays(nthWeekday(year, 10, 4, 4), 1), // Day after Thanksgiving
      observed(ymd(year, 11, 25))       // Christmas Day
    ];
  }

  function isBusinessDay(t, holidays) {
    var d = dow(t);
    if (d === 0 || d === 6) { return false; }
    for (var i = 0; i < holidays.length; i++) { if (holidays[i] === t) { return false; } }
    return true;
  }

  function addBusinessDays(t, n, holidayFn) {
    var cur = t;
    while (n > 0) {
      cur = addDays(cur, 1);
      if (isBusinessDay(cur, holidayFn(new Date(cur).getUTCFullYear()))) { n--; }
    }
    return cur;
  }

  function nextBusinessDayOrSame(t, holidayFn) {
    var cur = t;
    while (!isBusinessDay(cur, holidayFn(new Date(cur).getUTCFullYear()))) { cur = addDays(cur, 1); }
    return cur;
  }

  /* Current date/time in the warehouse timezone (America/New_York) */
  function warehouseNow() {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', hour12: false,
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric'
    }).formatToParts(new Date());
    var o = {};
    parts.forEach(function (p) { o[p.type] = p.value; });
    var hour = parseInt(o.hour, 10); if (hour === 24) { hour = 0; }
    return { day: ymd(parseInt(o.year, 10), parseInt(o.month, 10) - 1, parseInt(o.day, 10)), hour: hour };
  }

  function dispatchDate(cfg) {
    var now = warehouseNow();
    var day = now.day;
    var todayOk = isBusinessDay(day, usHolidays(new Date(day).getUTCFullYear())) && now.hour < cfg.cutoffHour;
    if (!todayOk) { day = nextBusinessDayOrSame(addDays(day, 1), usHolidays); }
    if (cfg.handlingDays > 0) { day = addBusinessDays(day, cfg.handlingDays, usHolidays); }
    return { day: day, isToday: day === now.day };
  }

  function fmt(t, withYear) {
    var d = new Date(t);
    var s = DAY_NAMES[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTH_NAMES[d.getUTCMonth()];
    if (withYear) { s += ' ' + d.getUTCFullYear(); }
    return s;
  }

  function estimate(mk, zone, cfg) {
    var dispatch = dispatchDate(cfg);
    var extra = zone.tier === 'regional' ? cfg.regionalExtra : (zone.tier === 'remote' ? cfg.remoteExtra : 0);
    var minDays = cfg.minDays + extra;
    var maxDays = cfg.maxDays + extra;
    var hol = function (y) { return localHolidays(mk, y, zone.state); };
    var from = addBusinessDays(dispatch.day, minDays, hol);
    var to = addBusinessDays(dispatch.day, maxDays, hol);
    var crossYear = new Date(from).getUTCFullYear() !== new Date(to).getUTCFullYear();
    return {
      dispatch: dispatch, from: from, to: to, minDays: minDays, maxDays: maxDays,
      rangeText: fmt(from, crossYear) + ' – ' + fmt(to, crossYear)
    };
  }

  /* ---------------------------------------------------------------
     DOM wiring
     --------------------------------------------------------------- */
  function num(v, fallback) { var n = parseInt(v, 10); return isNaN(n) ? fallback : n; }
  function readConfig(root) {
    var ds = root.dataset;
    return {
      minDays: num(ds.minDays, 5), maxDays: num(ds.maxDays, 9),
      regionalExtra: num(ds.regionalExtra, 0), remoteExtra: num(ds.remoteExtra, 0),
      cutoffHour: num(ds.cutoffHour, 16), handlingDays: num(ds.handlingDays, 0)
    };
  }
  function getStored(key) { try { return window.localStorage.getItem(key) || ''; } catch (e) { return ''; } }
  function setStored(key, v) { try { window.localStorage.setItem(key, v); } catch (e) { /* private mode */ } }

  function renderDispatch(root, cfg) {
    var el = root.querySelector('[data-intl-dispatch]');
    if (!el) { return; }
    var d = dispatchDate(cfg);
    el.textContent = d.isToday ? 'dispatches today' : 'dispatches ' + fmt(d.day, false);
  }

  function renderResult(root, mk, cfg, value) {
    var M = MARKETS[mk];
    var result = root.querySelector('[data-intl-result]');
    var input = root.querySelector('[data-intl-input]');
    if (!result) { return; }
    var zone = M.classify(value);
    if (!zone) {
      root.classList.remove('is-estimated');
      root.classList.add('has-error');
      var crown = mk === 'gb' ? GB_CROWN[(M.normalize(value).match(/^[A-Z]{1,2}/) || [''])[0]] : null;
      result.textContent = crown ? 'We ship to ' + crown + ' too. Shipping is calculated at checkout.' : M.invalid;
      if (input) { input.setAttribute('aria-invalid', 'true'); }
      return;
    }
    var est = estimate(mk, zone, cfg);
    root.classList.remove('has-error');
    root.classList.add('is-estimated');
    if (input) { input.removeAttribute('aria-invalid'); input.value = zone.postcode; }
    result.innerHTML =
      '<span class="sote-au-ship__result-label">Estimated delivery to <strong>' + zone.postcode + '</strong> (' + zone.label + '):</span> ' +
      '<strong class="sote-au-ship__dates">' + est.rangeText + '</strong>' +
      '<span class="sote-au-ship__days">' + est.minDays + '–' + est.maxDays + ' business days after dispatch</span>';
    setStored(M.storageKey, zone.postcode);
    document.dispatchEvent(new CustomEvent('sote:intl-postcode', { detail: { market: mk, postcode: zone.postcode, zone: zone, estimate: est } }));
  }

  function init(root) {
    if (root.dataset.intlInit === '1') { return; }
    var mk = root.dataset.market;
    var M = MARKETS[mk];
    if (!M) { return; }
    root.dataset.intlInit = '1';
    var cfg = readConfig(root);
    renderDispatch(root, cfg);

    var form = root.querySelector('[data-intl-form]');
    var input = root.querySelector('[data-intl-input]');
    if (form && input) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        renderResult(root, mk, cfg, input.value);
      });
      input.addEventListener('input', function () {
        input.value = input.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '');
        // Canada is fixed length, so estimate as soon as the 6th character lands. UK codes vary (5 to 7), so wait for Check / blur.
        if (M.fullLength && M.normalize(input.value).length === M.fullLength) { renderResult(root, mk, cfg, input.value); }
      });
      input.addEventListener('blur', function () {
        if (M.normalize(input.value).length >= 5) { renderResult(root, mk, cfg, input.value); }
      });
      var stored = getStored(M.storageKey);
      if (stored && !input.value) {
        input.value = stored;
        renderResult(root, mk, cfg, stored);
      }
    }
    // Keep several estimators on one page in sync
    document.addEventListener('sote:intl-postcode', function (e) {
      if (!input || e.detail.market !== mk || input.value === e.detail.postcode) { return; }
      input.value = e.detail.postcode;
      renderResult(root, mk, cfg, e.detail.postcode);
    });
  }

  function initAll() {
    var nodes = document.querySelectorAll('[data-sote-intl-ship]');
    for (var i = 0; i < nodes.length; i++) { init(nodes[i]); }
  }

  window.SoteIntlDelivery = {
    classify: function (mk, v) { return MARKETS[mk] ? MARKETS[mk].classify(v) : null; },
    holidays: localHolidays, estimate: estimate, init: initAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Theme editor: re-init when sections are re-rendered
  document.addEventListener('shopify:section:load', initAll);
})();
